import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckIcon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSelect, FormSwitch, PhoneNumberInput } from '@/components/forms'
import {
  useCreateUser,
  useLocales,
  getReadableLocaleName,
} from '@/hooks/api/useUserManagement'

// Validation schema
const userCreateSchema = z
  .object({
    email: z.string().email('app.validation.invalid_email'),
    first_name: z.string().min(1, 'app.validation.first_name_required'),
    last_name: z.string().min(1, 'app.validation.last_name_required'),
    phone_number: z.string().optional(),
    password: z
      .string()
      .min(8, 'app.validation.minLength')
      .regex(/[A-Z]/, 'app.validation.password.uppercase')
      .regex(/[!@#$%^&*]/, 'app.validation.password.special'),
    password_confirmation: z.string().min(1, 'app.validation.confirm_password_required'),
    locale: z.string().min(1, 'app.validation.language_required'),
    two_factor_enabled: z.boolean(),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'app.validation.password.mismatch',
    path: ['password_confirmation'],
  })

type UserCreateFormValues = z.infer<typeof userCreateSchema>

export function UserCreatePage() {
  const { t } = useTranslation()
  const { customerId, partnerId } = useParams<{
    customerId?: string
    partnerId?: string
  }>()
  const navigate = useNavigate()

  // Build base path for navigation
  const basePath = customerId
    ? `/customers/${customerId}/users`
    : partnerId
      ? `/partners/${partnerId}/users`
      : '/users'

  // Fetch locales
  const { data: locales = [] } = useLocales()

  // Mutation
  const createUser = useCreateUser()

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<UserCreateFormValues>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      phone_number: '',
      password: '',
      password_confirmation: '',
      locale: locales[0] || 'en',
      two_factor_enabled: false,
    },
  })

  // Locale options
  const localeOptions = locales.map((locale) => ({
    value: locale,
    label: getReadableLocaleName(locale),
  }))

  // Handle form submission
  const onSubmit = async (data: UserCreateFormValues) => {
    try {
      await createUser.mutateAsync({
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number || undefined,
        password: data.password,
        locale: data.locale,
        customer_id: customerId ? parseInt(customerId, 10) : undefined,
        partner_id: partnerId ? parseInt(partnerId, 10) : undefined,
        two_factor_enabled: data.two_factor_enabled,
        is_active: true,
      })
      toast.success(t('resources.users.create_success'))
      navigate(basePath)
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.users.create_error')
      toast.error(message)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('resources.users.create_title')} subtitle={t('resources.users.create_subtitle')} />

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="email"
              label={t('resources.users.fields.email')}
              type="email"
              placeholder={t('resources.users.placeholders.email')}
              isRequired
            />

            <FormInput
              control={control}
              name="first_name"
              label={t('resources.users.fields.first_name')}
              placeholder={t('resources.users.placeholders.first_name')}
              isRequired
            />

            <FormInput
              control={control}
              name="last_name"
              label={t('resources.users.fields.last_name')}
              placeholder={t('resources.users.placeholders.last_name')}
              isRequired
            />

            <PhoneNumberInput
              control={control}
              name="phone_number"
              label={t('resources.users.fields.phone_number')}
              description={t('resources.users.descriptions.phone_number')}
            />
          </div>

          {/* Password */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <div className="space-y-4">
              <FormInput
                control={control}
                name="password"
                label={t('resources.users.fields.password')}
                type="password"
                placeholder={t('resources.users.placeholders.password')}
                description={t('resources.users.descriptions.password')}
                isRequired
              />

              <FormInput
                control={control}
                name="password_confirmation"
                label={t('resources.users.fields.password_confirmation')}
                type="password"
                placeholder={t('resources.users.placeholders.password_confirmation')}
                isRequired
              />
            </div>
          </div>

          {/* Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSelect
              control={control}
              name="locale"
              label={t('resources.users.fields.locale')}
              options={localeOptions}
              isRequired
            />

            <div className="mt-4">
              <FormSwitch
                control={control}
                name="two_factor_enabled"
                label={t('resources.users.fields.requires_2fa')}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button color="secondary" type="button" onPress={() => navigate(basePath)}>
              {t('resources.generic.cancel')}
            </Button>
            <Button color="primary" type="submit" isDisabled={isSubmitting} iconLeading={CheckIcon}>
              {isSubmitting ? t('resources.generic.loading') : t('resources.generic.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default UserCreatePage
